import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  Play,
  Pause,
  Settings,
  History,
  BarChart3,
  ChevronRight,
  Plus,
  Mail,
  MessageSquare,
  Clock,
  GitBranch,
  Zap,
  CheckSquare,
  Tag,
  User,
  FileText,
  DollarSign,
  Bell,
  Webhook,
  X,
  Search,
  GripVertical,
  Trash2,
  MoreVertical,
  AlertCircle,
  Eye,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  RefreshCw,
  XCircle,
  Key,
  Building2,
  Ticket,
  Shield,
  Send,
  Users,
  Calendar,
  Monitor,
  Smartphone,
  Reply,
  Gift,
  HelpCircle,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import {
  ActionEditorUnsavedProvider,
  useActionEditorUnsaved,
} from '../../../context/ActionEditorUnsavedContext';
// Import shared automation components for full Agency-parity experience
import WorkflowActionsPanel from '../../../components/automations/WorkflowActionsPanel';
import EmailNodeEditor from '../../../components/automations/EmailNodeEditor';
import EmailActionComposer from '../../../components/automations/EmailActionComposer';
import SMSNodeEditor from '../../../components/automations/SMSNodeEditor';
import WaitNodeEditor from '../../../components/automations/WaitNodeEditor';
// Import the shared Email Preview component for Platform Automations
import { EmailPreview } from '../../../components/automations/EmailPreview';
// Import real workflow action editors
import BackendActionEditor, {
  isBackendActionStep,
  normalizeStepType,
} from '../../../components/automations/BackendActionEditor';
import TriggerEditor from '../../../components/automations/TriggerEditor';
import TriggerPickerItem from '../../../components/automations/TriggerPickerItem';
import WorkflowTriggerDetailPanel from '../../../components/automations/WorkflowTriggerDetailPanel';
import WorkflowEditorPanelShell from '../../../components/automations/WorkflowEditorPanelShell';
import { isPickerTriggerSelected } from '../../../utils/triggerPickerUtils';
import AuditLogEditor from '../../../components/automations/AuditLogEditor';
import {
  GrantPortalAccessEditor,
  RevokePortalAccessEditor,
  GrantOfferAccessEditor,
  RevokeOfferAccessEditor,
  ApplyTagEditor,
  RemoveTagEditor,
  InternalNotificationEditor,
  WaitEditor,
} from '../../../components/automations/WorkflowActionEditors';
import IfElseConditionEditor, { createDefaultIfElseConfig } from '../../../components/automations/IfElseConditionEditor';
import WorkflowConditionBranchCanvas from '../../../components/automations/WorkflowConditionBranchCanvas';
import { BRANCH_CARD_MIN_WIDTH, isConditionStep, shouldRenderMainChainEndNode } from '../../../utils/workflowConditionUtils';
import WorkflowBuilderStepList from '../../../components/automations/WorkflowBuilderStepList';
import WorkflowBuilderMainChain from '../../../components/automations/WorkflowBuilderMainChain';
import WorkflowStepCard from '../../../components/automations/WorkflowStepCard';
import WorkflowTriggerCanvasSection from '../../../components/automations/WorkflowTriggerCanvasSection';
import {
  buildTriggerSaveFields,
  createTriggerFromSelection,
  hydrateWorkflowTriggersFromServer,
  normalizeWorkflowTriggers,
  syncLegacyTriggerFields,
} from '../../../utils/workflowTriggerUtils';
import { isSmsStep } from '../../../utils/workflowSmsUtils';
import WorkflowBuilderUtilityShell from '../../../components/automations/WorkflowBuilderUtilityShell';
import useWorkflowEditorLayout from '../../../hooks/useWorkflowEditorLayout';
import WorkflowBuilderChrome from '../../../components/automations/WorkflowBuilderChrome';
import WorkflowRunTestPanel from '../../../components/automations/WorkflowRunTestPanel';
import {
  downloadPlatformWorkflow,
  getSafeWorkflowPortableError,
  triggerBrowserDownload,
} from '../../../services/workflowPortableService';
import { getPlatformBuilderPath } from '../../../services/workflowListService';
import WorkflowImportDialog from '../../../components/automations/WorkflowImportDialog';
import {
  createWorkflowImportOpeners,
  createWorkflowImportedHandler,
} from '../../../utils/workflowBuilderPortability';
import PreparePasswordResetEditor from '../../../components/automations/PreparePasswordResetEditor';
import FindUserPortalEditor from '../../../components/automations/FindUserPortalEditor';
import { useWorkflowBuilderCanvasControls } from '../../../hooks/useWorkflowBuilderCanvasControls';
import {
  applyGroupConfigToSteps,
  buildDisplaySteps,
  createStepsFromCatalogAction,
  GROUP_TYPES,
} from '../../../utils/workflowStepGrouping';
import {
  FORGOT_PASSWORD_TRIGGER,
  normalizeTriggerStep,
  triggerMatchesQuery,
} from '../../../utils/workflowTriggerCatalog';
import { PLATFORM_ACTION_EXTRA_GROUPS } from '../../../utils/platformWorkflowActionGroups';
import WorkflowSettingsTab from '../../../components/automations/WorkflowSettingsTab';
import WorkflowEnrollmentHistoryTab from '../../../components/automations/WorkflowEnrollmentHistoryTab';
import WorkflowExecutionLogsTab from '../../../components/automations/WorkflowExecutionLogsTab';
import WorkflowStatisticsPanel from '../../../components/automations/WorkflowStatisticsPanel';
import { normalizeWorkflowSettings, DEFAULT_WORKFLOW_SETTINGS } from '../../../utils/defaultWorkflowSettings';
import { getWorkflowStepDescription, getStepDisplayLabel } from '../../../utils/workflowBuilderUtils';
import { fetchExecutionPath } from '../../../utils/workflowInsightsApi';
import { WORKFLOW_MAIN_COLUMN_WIDTH } from '../../../utils/workflowCardAnchors';
import { getWorkflowActionLaneLayout, isSameTriggerConnectorGeometry } from '../../../utils/workflowTriggerConnectorLayout';
import {
  createBranchAddTarget,
  createMainAddTarget,
  duplicateBranchStepAfter,
  findBranchStepLocation,
  insertCatalogActionAtTarget,
  removeBranchStepFromCondition,
  updateBranchStepInCondition,
} from '../../../utils/workflowConditionBranchSteps';
// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const normalizeWorkflowStep = (step, index = 0) => {
  if (!step) return step;
  const normalizedType = normalizeStepType(step.type);
  const label = step.label || step.name || '';
  const fallbackId = `step_${String(index + 1).padStart(2, '0')}_${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'node'}`;
  const base = {
    ...step,
    id: step.id || fallbackId,
    type: normalizedType || step.type,
    label,
    name: step.name || label,
    order: step.order || index + 1,
  };
  if ((normalizedType || step.type) === 'trigger') {
    return normalizeTriggerStep(base);
  }
  return base;
};

const normalizeWorkflowSteps = (steps = []) => steps.map((step, index) => normalizeWorkflowStep(step, index));

// Platform system triggers - Simplified with dropdown configuration
const PLATFORM_TRIGGERS = {
  // Consolidated triggers with dropdown config
  postgrid: {
    label: 'PostGrid Event',
    description: 'Start when a PostGrid mail/delivery event happens',
    icon: Mail,
    isConsolidated: true,
    eventTypes: [
      { value: 'any', label: 'Any PostGrid Event' },
      { value: 'postgrid.webhook.received', label: 'Webhook Received' },
      { value: 'postgrid.letter.created', label: 'Letter Created' },
      { value: 'postgrid.letter.updated', label: 'Letter Updated' },
      { value: 'postgrid.letter.ready', label: 'Letter Ready' },
      { value: 'postgrid.letter.printing', label: 'Letter Printing' },
      { value: 'postgrid.letter.processed', label: 'Letter Processed' },
      { value: 'postgrid.letter.completed', label: 'Letter Completed' },
      { value: 'postgrid.letter.cancelled', label: 'Letter Cancelled' },
      { value: 'postgrid.letter.failed', label: 'Letter Failed' },
      { value: 'postgrid.status.changed', label: 'Mail Status Changed' },
      { value: 'postgrid.delivery.error', label: 'Delivery Error Detected' },
      { value: 'postgrid.usage.threshold', label: 'Usage Threshold Reached' },
    ],
    keywords: ['postgrid', 'mail', 'letter', 'delivery', 'completed', 'failed', 'cancelled', 'webhook'],
    filters: [
      { id: 'mail_type', label: 'Mail Type', options: ['All', 'Letter', 'Postcard', 'Cheque'] },
      { id: 'environment', label: 'Environment', options: ['All', 'Live', 'Test'] },
    ],
  },
  email: {
    label: 'Email Event',
    description: 'Start when an email delivery event happens',
    icon: Send,
    isConsolidated: true,
    eventTypes: [
      { value: 'any', label: 'Any Email Event' },
      { value: 'email.sent', label: 'Email Sent' },
      { value: 'email.delivered', label: 'Email Delivered' },
      { value: 'email.opened', label: 'Email Opened' },
      { value: 'email.clicked', label: 'Link Clicked' },
      { value: 'email.bounced', label: 'Email Bounced' },
      { value: 'email.failed', label: 'Email Failed' },
      { value: 'email.unsubscribed', label: 'User Unsubscribed' },
    ],
    keywords: ['email', 'sent', 'delivered', 'opened', 'clicked', 'bounced', 'failed'],
  },
  dispute: {
    label: 'Dispute Event',
    description: 'Start when a dispute workflow event happens',
    icon: FileText,
    isConsolidated: true,
    eventTypes: [
      { value: 'any', label: 'Any Dispute Event' },
      { value: 'dispute.round.started', label: 'Round Started' },
      { value: 'dispute.credit_report.imported', label: 'Credit Report Imported' },
      { value: 'dispute.letter_generation.completed', label: 'Letter Generation Completed' },
      { value: 'dispute.package.approved', label: 'Package Approved' },
      { value: 'dispute.letter.sent_to_postgrid', label: 'Letter Sent to PostGrid' },
      { value: 'dispute.missing_documents', label: 'Missing Documents' },
      { value: 'dispute.document.uploaded', label: 'Document Uploaded' },
    ],
    keywords: ['dispute', 'round', 'letter', 'package', 'bureau', 'document'],
  },
  crm: {
    label: 'Contact Event',
    description: 'Start when a CRM/contact event happens',
    icon: Users,
    isConsolidated: true,
    eventTypes: [
      { value: 'any', label: 'Any Contact Event' },
      { value: 'contact.created', label: 'Contact Created' },
      { value: 'contact.updated', label: 'Contact Updated' },
      { value: 'contact.tagged', label: 'Contact Tagged' },
      { value: 'contact.status.changed', label: 'Status Changed' },
      { value: 'task.created', label: 'Task Created' },
      { value: 'task.completed', label: 'Task Completed' },
      { value: 'agency_client.created', label: 'Agency Client Created' },
    ],
    keywords: ['contact', 'crm', 'client', 'tag', 'task', 'status'],
  },
  usage: {
    label: 'Usage / Billing Event',
    description: 'Start when a usage or billing event happens',
    icon: DollarSign,
    isConsolidated: true,
    eventTypes: [
      { value: 'any', label: 'Any Usage Event' },
      { value: 'usage.letter_credit.used', label: 'Credit Used' },
      { value: 'usage.letter_credit.refunded', label: 'Credit Refunded' },
      { value: 'usage.monthly_threshold', label: 'Monthly Threshold Reached' },
      { value: 'usage.agency_threshold', label: 'Agency Threshold Reached' },
      { value: 'billing.payment.failed', label: 'Payment Failed' },
      { value: 'billing.payment.succeeded', label: 'Payment Succeeded' },
    ],
    keywords: ['usage', 'billing', 'credit', 'payment', 'subscription', 'threshold'],
  },
  // Traditional category-based triggers
  authentication: {
    label: 'Authentication',
    icon: Key,
    triggers: [
      {
        id: FORGOT_PASSWORD_TRIGGER.eventKey,
        eventKey: FORGOT_PASSWORD_TRIGGER.eventKey,
        name: FORGOT_PASSWORD_TRIGGER.name,
        description: FORGOT_PASSWORD_TRIGGER.description,
        aliases: FORGOT_PASSWORD_TRIGGER.aliases,
        keywords: FORGOT_PASSWORD_TRIGGER.keywords,
      },
      { id: 'user.email_verification_requested', name: 'Email Verification', description: 'User requests email verification' },
      { id: 'user.invited', name: 'User Invited', description: 'User is invited to platform' },
      { id: 'user.account_locked', name: 'Account Locked', description: 'User account is locked' },
    ],
  },
  agency_lifecycle: {
    label: 'Agency Lifecycle',
    icon: Building2,
    triggers: [
      { id: 'agency.created', name: 'Agency Created', description: 'New agency is created' },
      { id: 'agency.owner_invited', name: 'Owner Invited', description: 'Agency owner is invited' },
      { id: 'agency.admin_invited', name: 'Admin Invited', description: 'Agency admin is invited' },
      { id: 'agency.trial_started', name: 'Trial Started', description: 'Agency trial starts' },
      { id: 'agency.trial_ending', name: 'Trial Ending', description: 'Agency trial is ending' },
      { id: 'agency.subscription_activated', name: 'Subscription Activated', description: 'Agency subscription activated' },
      { id: 'agency.subscription_failed', name: 'Payment Failed', description: 'Agency subscription payment failed' },
      { id: 'agency.subscription_canceled', name: 'Subscription Canceled', description: 'Agency subscription canceled' },
    ],
  },
  support_tickets: {
    label: 'Support / Tickets',
    icon: Ticket,
    triggers: [
      { id: 'ticket.created', name: 'Ticket Created', description: 'Support ticket created' },
      { id: 'ticket.assigned', name: 'Ticket Assigned', description: 'Ticket assigned to staff' },
      { id: 'ticket.reply_received', name: 'Reply Received', description: 'Ticket receives a reply' },
      { id: 'ticket.status_updated', name: 'Status Updated', description: 'Ticket status updated' },
      { id: 'ticket.escalated', name: 'Ticket Escalated', description: 'Ticket escalated' },
    ],
  },
  system_alerts: {
    label: 'System Alerts',
    icon: AlertTriangle,
    triggers: [
      { id: 'system.import_failed', name: 'Import Failed', description: 'Credit report import failed' },
      { id: 'system.import_completed', name: 'Import Completed', description: 'Credit report import completed' },
      { id: 'system.mailgun_failed', name: 'Mailgun Failed', description: 'Mailgun delivery failed' },
      { id: 'system.domain_verification_failed', name: 'Domain Verification Failed', description: 'Domain verification failed' },
      { id: 'system.domain_verified', name: 'Domain Verified', description: 'Domain verified successfully' },
    ],
  },
};

// Step types for system workflows - Complete action type dictionary
const STEP_TYPES = {
  trigger: { label: 'Trigger', icon: Zap, color: 'emerald' },
  action: { label: 'Backend Action', icon: Settings, color: 'orange' },
  backend_action: { label: 'Backend Action', icon: Settings, color: 'orange' },
  send_email: { label: 'Send Email', icon: Mail, color: 'blue' },
  email_action: { label: 'Send Email', icon: Mail, color: 'blue' },
  send_sms: { label: 'Send SMS', icon: MessageSquare, color: 'green' },
  wait: { label: 'Wait', icon: Clock, color: 'amber' },
  delay: { label: 'Wait', icon: Clock, color: 'amber' },
  wait_until: { label: 'Wait Until Date', icon: Calendar, color: 'amber' },
  condition: { label: 'If/Else', icon: GitBranch, color: 'purple' },
  branch: { label: 'Branch', icon: GitBranch, color: 'purple' },
  if_else: { label: 'If/Else', icon: GitBranch, color: 'purple' },
  send_notification: { label: 'Internal Notification', icon: Bell, color: 'cyan' },
  staff_alert: { label: 'Staff Alert', icon: AlertTriangle, color: 'red' },
  audit_log: { label: 'Audit Log', icon: FileText, color: 'slate' },
  security_lock: { label: 'Security Lock', icon: Shield, color: 'red' },
  update_record: { label: 'Update Record', icon: RefreshCw, color: 'indigo' },
  manual_review: { label: 'Manual Review', icon: Eye, color: 'amber' },
  create_task: { label: 'Create Task', icon: CheckSquare, color: 'teal' },
  end: { label: 'End', icon: CheckCircle, color: 'slate' },
  webhook: { label: 'Webhook', icon: Webhook, color: 'gray' },
  internal_notification: { label: 'Internal Notification', icon: Bell, color: 'cyan' },
  update_status: { label: 'Update Status', icon: Zap, color: 'orange' },
  apply_tag: { label: 'Apply Tag', icon: Tag, color: 'pink' },
  add_tag: { label: 'Add Tag', icon: Tag, color: 'pink' },
  remove_tag: { label: 'Remove Tag', icon: Tag, color: 'slate' },
  update_contact: { label: 'Update Contact', icon: User, color: 'indigo' },
  assign_user: { label: 'Assign to Staff', icon: User, color: 'indigo' },
  create_opportunity: { label: 'Create Opportunity', icon: DollarSign, color: 'emerald' },
  move_opportunity_stage: { label: 'Move Stage', icon: ChevronRight, color: 'teal' },
  start_workflow: { label: 'Start Workflow', icon: Play, color: 'emerald' },
  stop_workflow: { label: 'Stop Workflow', icon: XCircle, color: 'red' },
  grant_portal_access: { label: 'Grant Portal Access', icon: Key, color: 'emerald' },
  revoke_portal_access: { label: 'Revoke Portal Access', icon: Shield, color: 'red' },
  lock_self_repair_access: { label: 'Lock Self Repair', icon: Shield, color: 'red' },
  unlock_self_repair_access: { label: 'Unlock Self Repair', icon: Key, color: 'emerald' },
  send_portal_invite: { label: 'Send Portal Invite', icon: Send, color: 'blue' },
  grant_offer_access: { label: 'Grant Offer / Product', icon: Gift, color: 'emerald' },
  revoke_offer_access: { label: 'Revoke Offer / Product', icon: XCircle, color: 'red' },
};

const resolveStepTypeConfig = (step) => {
  const rawType = normalizeStepType(step?.type);
  const config = STEP_TYPES[rawType];
  if (config) return config;
  if (step?.config?.requires_backend) {
    return { label: 'Required Backend Action', icon: AlertCircle, color: 'orange' };
  }
  return { label: 'Backend Action', icon: Settings, color: 'orange' };
};

// Step Node Component
const StepNode = ({
  step,
  onRemove,
  onEdit,
  isMoving,
  isSelected,
  onDragHandleStart,
  onContextMenuAction,
  hasClipboard,
  readOnly = false,
  highlighted = false,
  pathState = null,
  columnWidth,
  isBranchStep = false,
}) => {
  const stepConfig = resolveStepTypeConfig(step);
  const displayLabel = getStepDisplayLabel(step) || stepConfig.label;

  return (
    <WorkflowStepCard
      step={step}
      stepConfig={stepConfig}
      title={displayLabel}
      typeLabel={stepConfig.label}
      description={getWorkflowStepDescription(step, stepConfig)}
      requiresBackend={Boolean(step.config?.requires_backend)}
      isMoving={isMoving}
      isSelected={isSelected}
      highlighted={highlighted}
      pathState={pathState}
      readOnly={readOnly}
      hasClipboard={hasClipboard}
      onEdit={onEdit}
      onRemove={onRemove}
      onDragHandleStart={onDragHandleStart}
      onContextMenuAction={onContextMenuAction}
      columnWidth={columnWidth}
      isBranchStep={isBranchStep}
    />
  );
};

// Trigger Picker Panel - Two-step UX: Select Category -> Configure Trigger
const PlatformTriggerPicker = ({
  isOpen,
  onClose,
  onSelectTrigger,
  activeTriggers = [],
  editingTriggerId = null,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'trigger-picker',
  chromeHeight,
}) => {

  const triggerPickerShell = (children, shellWidth, testId) => (
    <WorkflowEditorPanelShell
      isOpen={isOpen}
      width={shellWidth ?? width ?? 480}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="bg-zinc-900 border-l border-zinc-700"
      testId={testId}
    >
      <div className="flex h-full min-h-0 flex-col">{children}</div>
    </WorkflowEditorPanelShell>
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [configPanelOpen, setConfigPanelOpen] = useState(false);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [detailTrigger, setDetailTrigger] = useState(null);
  const [detailCategoryLabel, setDetailCategoryLabel] = useState('');
  
  // Configuration state
  const [triggerName, setTriggerName] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('any');
  const [filters, setFilters] = useState([]);

  // Filter field options for each category
  const FILTER_OPTIONS = {
    postgrid: [
      { value: 'mail_type', label: 'Mail Type', options: ['Letter', 'Postcard', 'Cheque', 'Self Mailer'] },
      { value: 'status', label: 'Status', options: ['Ready', 'Printing', 'Processed', 'Completed', 'Cancelled', 'Failed'] },
      { value: 'previous_status', label: 'Previous Status', options: ['Ready', 'Printing', 'Processed', 'In Transit'] },
      { value: 'new_status', label: 'New Status', options: ['Completed', 'Cancelled', 'Failed', 'Returned'] },
      { value: 'environment', label: 'Environment', options: ['Live', 'Test'] },
      { value: 'bureau', label: 'Bureau', options: ['Equifax', 'Experian', 'TransUnion'] },
      { value: 'method', label: 'Method', options: ['Data Breach', 'Metro 2', 'Isolated', 'Structural', 'Recovery'] },
      { value: 'agency', label: 'Agency', type: 'text' },
      { value: 'user_type', label: 'User Type', options: ['Agency Client', 'Agency Owner', 'Self Repair', 'Admin'] },
      { value: 'cost', label: 'Cost', type: 'number' },
      { value: 'credits_used', label: 'Credits Used', type: 'number' },
      { value: 'error_message', label: 'Error Message', type: 'text' },
    ],
    email: [
      { value: 'email_status', label: 'Email Status', options: ['Sent', 'Delivered', 'Opened', 'Clicked', 'Bounced', 'Failed'] },
      { value: 'template', label: 'Template', type: 'text' },
      { value: 'sequence', label: 'Sequence', type: 'text' },
      { value: 'recipient_type', label: 'Recipient Type', options: ['User', 'Agency Owner', 'Admin', 'Custom'] },
      { value: 'agency', label: 'Agency', type: 'text' },
      { value: 'user_type', label: 'User Type', options: ['Agency Client', 'Self Repair', 'Staff'] },
    ],
    dispute: [
      { value: 'bureau', label: 'Bureau', options: ['Equifax', 'Experian', 'TransUnion'] },
      { value: 'method', label: 'Method', options: ['Data Breach', 'Metro 2', 'Isolated', 'Structural', 'Recovery'] },
      { value: 'round', label: 'Round', type: 'number' },
      { value: 'package_status', label: 'Package Status', options: ['Draft', 'Pending Review', 'Approved', 'Sent', 'Complete'] },
      { value: 'letter_count', label: 'Letter Count', type: 'number' },
      { value: 'missing_document_type', label: 'Missing Document Type', options: ['ID', 'Proof of Address', 'SSN Card', 'Data Breach Proof'] },
      { value: 'user_type', label: 'User Type', options: ['Agency Client', 'Self Repair'] },
      { value: 'agency', label: 'Agency', type: 'text' },
    ],
    crm: [
      { value: 'contact_type', label: 'Contact Type', options: ['Lead', 'Client', 'Former Client'] },
      { value: 'contact_status', label: 'Contact Status', options: ['Active', 'Inactive', 'Pending'] },
      { value: 'tag', label: 'Tag', type: 'text' },
      { value: 'source', label: 'Source', options: ['Website', 'Referral', 'Whop', 'Manual'] },
      { value: 'agency', label: 'Agency', type: 'text' },
      { value: 'smart_list', label: 'Smart List', type: 'text' },
    ],
    usage: [
      { value: 'plan', label: 'Plan', options: ['Free', 'Starter', 'Pro', 'Enterprise'] },
      { value: 'user_type', label: 'User Type', options: ['Agency Owner', 'Self Repair', 'Managed Client'] },
      { value: 'agency', label: 'Agency', type: 'text' },
      { value: 'credits_used', label: 'Credits Used', type: 'number' },
      { value: 'cost', label: 'Cost', type: 'number' },
      { value: 'payment_status', label: 'Payment Status', options: ['Paid', 'Failed', 'Pending', 'Refunded'] },
      { value: 'subscription_status', label: 'Subscription Status', options: ['Active', 'Cancelled', 'Paused', 'Trial'] },
    ],
  };

  const OPERATORS = [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'exists', label: 'exists' },
    { value: 'not_exists', label: 'does not exist' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
  ];

  // Reset config when opening a new category
  const openConfigPanel = (categoryId, category) => {
    setSelectedCategory({ id: categoryId, ...category });
    setTriggerName(category.label);
    setSelectedEventType('any');
    setFilters([]);
    setConfigPanelOpen(true);
  };

  const addFilter = () => {
    setFilters([...filters, { id: Date.now(), field: '', operator: 'equals', value: '' }]);
  };

  const removeFilter = (filterId) => {
    setFilters(filters.filter(f => f.id !== filterId));
  };

  const updateFilter = (filterId, key, value) => {
    setFilters(filters.map(f => f.id === filterId ? { ...f, [key]: value } : f));
  };

  const handleSaveTrigger = () => {
    const triggerConfig = {
      id: selectedCategory.id,
      trigger_type: `${selectedCategory.id}.event`,
      trigger_name: triggerName,
      name: triggerName,
      description: selectedCategory.description,
      category: selectedCategory.id,
      isConsolidated: true,
      eventTypes: selectedCategory.eventTypes,
      config: {
        event_type: selectedEventType,
        filters: filters.filter(f => f.field && f.operator).map(f => ({
          field: f.field,
          operator: f.operator,
          value: f.value
        }))
      }
    };
    onSelectTrigger(triggerConfig);
    setConfigPanelOpen(false);
    onClose();
  };

  const openTriggerDetail = (trigger, categoryLabel) => {
    setDetailTrigger(trigger);
    setDetailCategoryLabel(categoryLabel || '');
    setDetailPanelOpen(true);
  };

  const handleSaveTriggerDetail = (updatedTrigger) => {
    onSelectTrigger(updatedTrigger);
    setDetailPanelOpen(false);
    setDetailTrigger(null);
    setDetailCategoryLabel('');
  };

  if (!isOpen) return null;

  if (detailPanelOpen && detailTrigger) {
    return (
      <WorkflowTriggerDetailPanel
        trigger={detailTrigger}
        categoryLabel={detailCategoryLabel}
        variant="dark"
        width={width}
        overlayMode={overlayMode}
        onOverlayDismiss={onOverlayDismiss}
        activePanelType="trigger-detail"
        chromeHeight={chromeHeight}
        onBack={() => {
          setDetailPanelOpen(false);
          setDetailTrigger(null);
          setDetailCategoryLabel('');
        }}
        onClose={onClose}
        onSave={handleSaveTriggerDetail}
      />
    );
  }

  // Configuration Panel View
  if (configPanelOpen && selectedCategory) {
    const categoryFilters = FILTER_OPTIONS[selectedCategory.id] || [];
    
    return triggerPickerShell(
      <>
        {/* Header */}
        <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setConfigPanelOpen(false)}
              className="p-1.5 hover:bg-zinc-800 rounded-lg"
            >
              <ArrowLeft size={18} className="text-zinc-400" />
            </button>
            <h3 className="font-semibold text-white">{selectedCategory.label}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="text-xs border-zinc-700 text-zinc-400">
              <HelpCircle size={14} className="mr-1" />
              Learn more
            </Button>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
              <X size={18} className="text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description */}
          <p className="text-sm text-zinc-400">{selectedCategory.description}</p>

          {/* Choose a Workflow Trigger */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              Choose a Workflow Trigger
            </label>
            <Select value={selectedCategory.id} disabled>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue>{selectedCategory.label}</SelectValue>
              </SelectTrigger>
            </Select>
          </div>

          {/* Workflow Trigger Name */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              Workflow Trigger Name
            </label>
            <Input
              value={triggerName}
              onChange={(e) => setTriggerName(e.target.value)}
              placeholder="Enter trigger name"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* Event Type */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              Event Type
            </label>
            <Select value={selectedEventType} onValueChange={setSelectedEventType}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {selectedCategory.eventTypes?.map((event) => (
                  <SelectItem 
                    key={event.value} 
                    value={event.value}
                    className="text-white hover:bg-zinc-700 focus:bg-zinc-700"
                  >
                    {event.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filters Section */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              Filters
            </label>
            
            {filters.map((filter) => (
              <div key={filter.id} className="flex items-center gap-2">
                {/* Field */}
                <Select 
                  value={filter.field} 
                  onValueChange={(val) => updateFilter(filter.id, 'field', val)}
                >
                  <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-white text-sm">
                    <SelectValue placeholder="Field" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {categoryFilters.map((f) => (
                      <SelectItem 
                        key={f.value} 
                        value={f.value}
                        className="text-white hover:bg-zinc-700"
                      >
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Operator */}
                <Select 
                  value={filter.operator} 
                  onValueChange={(val) => updateFilter(filter.id, 'operator', val)}
                >
                  <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-white text-sm">
                    <SelectValue placeholder="Operator" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {OPERATORS.map((op) => (
                      <SelectItem 
                        key={op.value} 
                        value={op.value}
                        className="text-white hover:bg-zinc-700"
                      >
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Value */}
                {(() => {
                  const fieldDef = categoryFilters.find(f => f.value === filter.field);
                  if (fieldDef?.options) {
                    return (
                      <Select 
                        value={filter.value} 
                        onValueChange={(val) => updateFilter(filter.id, 'value', val)}
                      >
                        <SelectTrigger className="flex-1 bg-zinc-800 border-zinc-700 text-white text-sm">
                          <SelectValue placeholder="Value" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {fieldDef.options.map((opt) => (
                            <SelectItem 
                              key={opt} 
                              value={opt}
                              className="text-white hover:bg-zinc-700"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  }
                  return (
                    <Input
                      value={filter.value}
                      onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                      placeholder="Value"
                      type={fieldDef?.type === 'number' ? 'number' : 'text'}
                      className="flex-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  );
                })()}

                {/* Delete button */}
                <button
                  onClick={() => removeFilter(filter.id)}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Trash2 size={16} className="text-zinc-500 hover:text-red-400" />
                </button>
              </div>
            ))}

            <button
              onClick={addFilter}
              className="flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <Plus size={16} />
              Add filters
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-700 flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => setConfigPanelOpen(false)}
            className="border-zinc-700 text-zinc-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTrigger}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            Save Trigger
          </Button>
        </div>
      </>,
      520,
      'platform-trigger-config'
    );
  }

  // Main Select Trigger View
  return triggerPickerShell(
    <>
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <h3 className="font-semibold text-white">Select Trigger</h3>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="p-4 border-b border-zinc-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            placeholder="Search triggers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {Object.entries(PLATFORM_TRIGGERS).map(([categoryId, category]) => {
          const CategoryIcon = category.icon;
          
          // Handle consolidated triggers (one card with config panel)
          if (category.isConsolidated) {
            const matchesSearch = !searchQuery || 
              category.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
              (category.keywords && category.keywords.some(kw => kw.toLowerCase().includes(searchQuery.toLowerCase()))) ||
              (category.eventTypes && category.eventTypes.some(et => et.label.toLowerCase().includes(searchQuery.toLowerCase())));
            
            if (!matchesSearch) return null;
            
            return (
              <div key={categoryId} className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <CategoryIcon size={16} className="text-emerald-400" />
                  <h4 className="text-sm font-semibold text-zinc-400 uppercase">{category.label}</h4>
                </div>
                <div className="space-y-2">
                  <TriggerPickerItem
                    trigger={{
                      id: categoryId,
                      name: category.label,
                      description: category.description,
                      eventKey: categoryId,
                    }}
                    categoryLabel={category.label}
                    variant="dark"
                    icon={CategoryIcon}
                    iconClassName="bg-emerald-500/20"
                    isSelected={isPickerTriggerSelected(
                      { id: categoryId, eventKey: categoryId },
                      activeTriggers,
                      editingTriggerId,
                    )}
                    onOpen={() => openConfigPanel(categoryId, category)}
                  />
                </div>
              </div>
            );
          }
          
          // Handle traditional triggers array
          if (!category.triggers) return null;
          
          const triggers = category.triggers.filter((t) => triggerMatchesQuery(t, searchQuery));
          
          if (triggers.length === 0) return null;

          return (
            <div key={categoryId} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <CategoryIcon size={16} className="text-emerald-400" />
                <h4 className="text-sm font-semibold text-zinc-400 uppercase">{category.label}</h4>
              </div>
              <div className="space-y-2">
                {triggers.map((trigger) => (
                  <TriggerPickerItem
                    key={trigger.id}
                    trigger={{
                      ...trigger,
                      eventKey: trigger.eventKey || trigger.id,
                    }}
                    categoryLabel={category.label}
                    variant="dark"
                    icon={Zap}
                    isSelected={isPickerTriggerSelected(trigger, activeTriggers, editingTriggerId)}
                    onOpen={(item) => openTriggerDetail(item, category.label)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>,
    480,
    'platform-trigger-picker'
  );
};

// Main Component
const PlatformWorkflowBuilderPageInner = () => {
  const { guardAction } = useActionEditorUnsaved();
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  const isNewWorkflow = workflowId === 'new';
  const mode = searchParams.get('mode');

  // Workflow state
  const [workflow, setWorkflow] = useState({
    name: 'Untitled System Workflow',
    description: '',
    status: 'draft',
    category: 'authentication',
    trigger: null,
    triggers: [],
    sender_scope: 'platform',
    steps: [],
    canvas_notes: [],
    comments: [],
    settings: { ...DEFAULT_WORKFLOW_SETTINGS },
    updated_at: null,
  });
  const [loading, setLoading] = useState(!isNewWorkflow);
  const [saving, setSaving] = useState(false);
  const [isDownloadingWorkflow, setIsDownloadingWorkflow] = useState(false);
  const [workflowImportOpen, setWorkflowImportOpen] = useState(false);
  const [workflowImportMode, setWorkflowImportMode] = useState('file');
  const [activeTab, setActiveTab] = useState('builder');
  const [executionPathView, setExecutionPathView] = useState(null);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [showTriggerPicker, setShowTriggerPicker] = useState(false);
  const [triggerPickerMode, setTriggerPickerMode] = useState('add');
  const [editingTriggerId, setEditingTriggerId] = useState(null);
  const [pendingAddTarget, setPendingAddTarget] = useState(null);
  const [editingBranchContext, setEditingBranchContext] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [showRunTestPanel, setShowRunTestPanel] = useState(false);
  const [runTestExpanded, setRunTestExpanded] = useState(false);
  const nameEditSnapshot = useRef(null);
  const [executions, setExecutions] = useState([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  
  // Step editors
  const [editingStep, setEditingStep] = useState(null);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [showSMSEditor, setShowSMSEditor] = useState(false);
  const [showWaitEditor, setShowWaitEditor] = useState(false);
  const [showGrantPortalEditor, setShowGrantPortalEditor] = useState(false);
  const [showRevokePortalEditor, setShowRevokePortalEditor] = useState(false);
  const [showGrantOfferEditor, setShowGrantOfferEditor] = useState(false);
  const [showRevokeOfferEditor, setShowRevokeOfferEditor] = useState(false);
  const [showApplyTagEditor, setShowApplyTagEditor] = useState(false);
  const [showRemoveTagEditor, setShowRemoveTagEditor] = useState(false);
  const [showConditionEditor, setShowConditionEditor] = useState(false);
  const [showNotificationEditor, setShowNotificationEditor] = useState(false);
  const [showBackendActionEditor, setShowBackendActionEditor] = useState(false);
  const [showPreparePasswordResetEditor, setShowPreparePasswordResetEditor] = useState(false);
  const [showFindUserPortalEditor, setShowFindUserPortalEditor] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showTriggerEditor, setShowTriggerEditor] = useState(false);
  const [showAuditLogEditor, setShowAuditLogEditor] = useState(false);
  const [triggerConnectorLayout, setTriggerConnectorLayout] = useState(null);
  const triggerConnectorLayoutRef = useRef(null);
  const pendingAddTargetRef = useRef(null);

  const handleTriggerConnectorLayoutChange = useCallback((layout) => {
    if (isSameTriggerConnectorGeometry(triggerConnectorLayoutRef.current, layout)) {
      return;
    }
    triggerConnectorLayoutRef.current = layout;
    setTriggerConnectorLayout(layout);
  }, []);

  const actionLaneLayout = useMemo(
    () => getWorkflowActionLaneLayout(triggerConnectorLayout),
    [triggerConnectorLayout],
  );

  const closeAllPanels = () => {
    setShowActionsPanel(false);
    setShowTriggerPicker(false);
    setShowRunTestPanel(false);
    setRunTestExpanded(false);
    pendingAddTargetRef.current = null;
    setPendingAddTarget(null);
    setEditingBranchContext(null);
    setShowEmailEditor(false);
    setShowSMSEditor(false);
    setShowWaitEditor(false);
    setShowGrantPortalEditor(false);
    setShowRevokePortalEditor(false);
    setShowGrantOfferEditor(false);
    setShowRevokeOfferEditor(false);
    setShowApplyTagEditor(false);
    setShowRemoveTagEditor(false);
    setShowConditionEditor(false);
    setShowNotificationEditor(false);
    setShowBackendActionEditor(false);
    setShowPreparePasswordResetEditor(false);
    setShowFindUserPortalEditor(false);
    setShowTriggerEditor(false);
    setShowAuditLogEditor(false);
    setEditingStep(null);
    setEditingGroup(null);
  };

  const closeEditorOverlay = closeAllPanels;

  // Fetch existing workflow
  useEffect(() => {
    const fetchWorkflow = async () => {
      if (isNewWorkflow || !token) return;
      
      setLoading(true);
      try {
        const res = await axios.get(`${API}/platform/system-automations/workflows/${workflowId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const wf = res.data;
        // Hydrate triggers from the richest available source (triggers array,
        // then legacy trigger / trigger_event) so an empty triggers array from
        // the server does not wipe a persisted legacy trigger.
        const hydratedTriggers = hydrateWorkflowTriggersFromServer(wf);
        // TEMP DEBUG (trigger persistence investigation) - remove later
        console.debug(
          '[trigger-persistence] hydrated workflow',
          workflowId,
          hydratedTriggers.map((t) => t.trigger_type || t.id),
        );
        const loadedWorkflow = {
          name: wf.name || 'Untitled',
          description: wf.description || '',
          status: wf.status || 'draft',
          category: wf.category || 'authentication',
          sender_scope: wf.sender_scope || 'platform',
          steps: normalizeWorkflowSteps(wf.steps || []),
          canvas_notes: wf.canvas_notes || [],
          comments: wf.comments || [],
          settings: normalizeWorkflowSettings(wf.settings),
          updated_at: wf.updated_at || null,
        };
        setWorkflow(syncLegacyTriggerFields(loadedWorkflow, hydratedTriggers));
      } catch (err) {
        console.error('Failed to fetch workflow:', err);
        toast.error('Failed to load workflow');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [workflowId, isNewWorkflow, token]);

  const { undoRedo, recordWorkflowChange } = useWorkflowBuilderCanvasControls({
    workflow,
    setWorkflow,
    setHasUnsavedChanges,
    enabled: activeTab === 'builder',
  });

  useEffect(() => {
    if (!loading) {
      undoRedo.clearHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, loading]);

  useEffect(() => {
    const fetchExecutions = async () => {
      if (!token || isNewWorkflow || activeTab !== 'logs') return;
      setLoadingExecutions(true);
      try {
        const res = await axios.get(
          `${API}/platform/system-automations/workflows/${workflowId}/executions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setExecutions(res.data?.executions || []);
      } catch (err) {
        console.error('Failed to fetch workflow executions:', err);
        setExecutions([]);
      } finally {
        setLoadingExecutions(false);
      }
    };
    fetchExecutions();
  }, [workflowId, isNewWorkflow, token, activeTab]);

  const handleSettingsChange = (patch) => {
    recordWorkflowChange('Updated workflow settings');
    setWorkflow((prev) => ({
      ...prev,
      settings: { ...normalizeWorkflowSettings(prev.settings), ...patch },
    }));
    setHasUnsavedChanges(true);
  };

  const handleSettingsMetaChange = (patch) => {
    recordWorkflowChange('Updated workflow settings');
    setWorkflow((prev) => ({ ...prev, ...patch }));
    setHasUnsavedChanges(true);
  };

  const handleViewExecutionPath = async (row) => {
    const executionId = row.execution_id || row.id;
    if (!executionId || !token || isNewWorkflow) return;
    try {
      const path = await fetchExecutionPath({
        token,
        workflowId,
        executionId,
      });
      setExecutionPathView({
        ...path,
        contact_id: row.contact_id || path.contact_id,
        contact_name: row.contact_name,
        enrollment_date: row.date_enrolled || row.enrollment_date,
        workflow_version: row.workflow_version,
      });
      setActiveTab('builder');
    } catch (err) {
      toast.error('Failed to load execution path');
    }
  };

  const handleAddStep = (index) => {
    closeAllPanels();
    pendingAddTargetRef.current = createMainAddTarget(index);
    setPendingAddTarget(pendingAddTargetRef.current);
    setShowActionsPanel(true);
  };

  const handleAddBranchStep = (conditionStepId, branchId, insertIndex, parentPath = []) => {
    closeAllPanels();
    pendingAddTargetRef.current = createBranchAddTarget({
      conditionStepId,
      branchId,
      insertIndex,
      parentPath,
    });
    setPendingAddTarget(pendingAddTargetRef.current);
    setShowActionsPanel(true);
  };

  const handleSelectAction = (action) => {
    guardAction(() => {
      recordWorkflowChange('Added step');
      const result = insertCatalogActionAtTarget(workflow.steps, pendingAddTargetRef.current, action);
      setWorkflow((prev) => ({ ...prev, steps: result.newSteps }));
      setShowActionsPanel(false);
      pendingAddTargetRef.current = null;
      setPendingAddTarget(null);
      setHasUnsavedChanges(true);

      if (result.isGroup) {
        const groupItem = buildDisplaySteps(result.insertedSteps, { advancedMode: true }).display[0];
        if (groupItem?.type === 'group') {
          openEditGroupInternal(groupItem);
        }
        return;
      }

      setEditingBranchContext(result.insertedTarget?.type === 'branch' ? {
        conditionStepId: result.insertedTarget.conditionStepId,
        branchId: result.insertedTarget.branchId,
      } : null);
      openStepEditorInternal(result.primaryStep);
    });
  };

  const handleSelectTrigger = (trigger) => {
    recordWorkflowChange('Updated trigger');
    setWorkflow((prev) => {
      const currentTriggers = normalizeWorkflowTriggers(prev);
      let nextTriggers;
      if (triggerPickerMode === 'edit' && editingTriggerId) {
        nextTriggers = currentTriggers.map((item) => (
          item.id === editingTriggerId
            ? createTriggerFromSelection(trigger, { preserveId: editingTriggerId })
            : item
        ));
      } else {
        nextTriggers = [...currentTriggers, createTriggerFromSelection(trigger)];
      }
      return syncLegacyTriggerFields(prev, nextTriggers);
    });
    setHasUnsavedChanges(true);
    setEditingTriggerId(null);
    setTriggerPickerMode('add');
  };

  const handleTriggersChange = (nextTriggers) => {
    recordWorkflowChange('Updated triggers');
    setWorkflow((prev) => syncLegacyTriggerFields(prev, nextTriggers));
    setHasUnsavedChanges(true);
    if (editingTriggerId && !nextTriggers.some((item) => item.id === editingTriggerId)) {
      setShowTriggerPicker(false);
      setShowTriggerEditor(false);
      setEditingTriggerId(null);
      setTriggerPickerMode('add');
    }
  };

  const openAddTriggerPicker = () => {
    closeAllPanels();
    setTriggerPickerMode('add');
    setEditingTriggerId(null);
    setShowTriggerPicker(true);
  };

  const openEditTriggerPicker = (trigger) => {
    closeAllPanels();
    setTriggerPickerMode('edit');
    setEditingTriggerId(trigger.id);
    setShowTriggerPicker(true);
  };

  const openEditGroupInternal = (groupItem) => {
    closeAllPanels();
    setEditingGroup(groupItem);
    if (groupItem.groupType === GROUP_TYPES.PREPARE_PASSWORD_RESET) {
      setShowPreparePasswordResetEditor(true);
      return;
    }
    if (groupItem.groupType === GROUP_TYPES.FIND_USER_PORTAL) {
      setShowFindUserPortalEditor(true);
    }
  };

  const openEditGroup = (groupItem) => {
    guardAction(() => openEditGroupInternal(groupItem));
  };

  const handleSaveGroup = (nextConfig) => {
    if (!editingGroup) return;
    recordWorkflowChange('Updated grouped step settings');
    setWorkflow((prev) => ({
      ...prev,
      steps: applyGroupConfigToSteps(prev.steps, editingGroup, nextConfig),
    }));
    setHasUnsavedChanges(true);
  };

  const openStepEditorInternal = (step, branchContext = null) => {
    closeAllPanels();
    const normalizedStep = normalizeWorkflowStep(step);
    setEditingStep(normalizedStep);
    setEditingBranchContext(branchContext);

    const { display } = buildDisplaySteps([normalizedStep], { advancedMode: true });
    const containingGroup = buildDisplaySteps(workflow.steps, { advancedMode: true }).display
      .find((item) => item.type === 'group' && item.stepIds.includes(normalizedStep.id));
    if (containingGroup && !isBackendActionStep(normalizedStep)) {
      openEditGroupInternal(containingGroup);
      return;
    }
    
    if (isBackendActionStep(normalizedStep)) {
      const groupFromSteps = buildDisplaySteps(workflow.steps, { advancedMode: true }).display
        .find((item) => item.type === 'group' && item.stepIds.includes(normalizedStep.id));
      if (groupFromSteps) {
        openEditGroupInternal(groupFromSteps);
        return;
      }
      setShowBackendActionEditor(true);
      return;
    }

    switch (normalizedStep.type) {
      case 'trigger':
        setShowTriggerEditor(true);
        break;
      case 'audit_log':
        setShowAuditLogEditor(true);
        break;
      case 'send_email':
      case 'email_action':
        setShowEmailEditor(true);
        break;
      case 'send_sms':
      case 'sms':
      case 'sms_action':
        setShowSMSEditor(true);
        break;
      case 'wait':
        setShowWaitEditor(true);
        break;
      case 'grant_portal_access':
        setShowGrantPortalEditor(true);
        break;
      case 'revoke_portal_access':
        setShowRevokePortalEditor(true);
        break;
      case 'grant_offer_access':
        setShowGrantOfferEditor(true);
        break;
      case 'revoke_offer_access':
        setShowRevokeOfferEditor(true);
        break;
      case 'apply_tag':
      case 'add_tag':
        setShowApplyTagEditor(true);
        break;
      case 'remove_tag':
        setShowRemoveTagEditor(true);
        break;
      case 'condition':
      case 'if_else':
        setShowConditionEditor(true);
        break;
      case 'internal_notification':
      case 'send_notification':
        setShowNotificationEditor(true);
        break;
      default:
        if (isSmsStep(normalizedStep)) {
          setShowSMSEditor(true);
          return;
        }
        if (normalizedStep.config?.requires_backend) {
          setShowBackendActionEditor(true);
          return;
        }
        toast.info(`Editor for "${normalizedStep.type}" is not yet implemented`);
        break;
    }
  };

  const openStepEditor = (step) => {
    if (step?.id && step.id === editingStep?.id) return;
    const branchLocation = findBranchStepLocation(workflow.steps, step?.id);
    guardAction(() => openStepEditorInternal(
      step,
      branchLocation
        ? { conditionStepId: branchLocation.conditionStepId, branchId: branchLocation.branchId }
        : null,
    ));
  };

  const handleSaveStep = (updatedStep) => {
    recordWorkflowChange('Updated step settings');
    const normalized = normalizeWorkflowStep(updatedStep);
    setWorkflow((prev) => ({
      ...prev,
      steps: editingBranchContext
        ? updateBranchStepInCondition(prev.steps, editingBranchContext, normalized)
        : prev.steps.map((s) => (s.id === normalized.id ? normalized : s)),
    }));
    setHasUnsavedChanges(true);
  };

  const handlePersistedStepSave = (updatedStep) => {
    recordWorkflowChange('Updated step settings');
    const normalized = normalizeWorkflowStep(updatedStep);
    setWorkflow((prev) => ({
      ...prev,
      steps: editingBranchContext
        ? updateBranchStepInCondition(prev.steps, editingBranchContext, normalized)
        : prev.steps.map((s) => (s.id === normalized.id ? normalized : s)),
    }));
    setHasUnsavedChanges(false);
  };

  const handleRemoveStep = (stepId) => {
    recordWorkflowChange('Deleted step');
    const branchLocation = findBranchStepLocation(workflow.steps, stepId);
    if (branchLocation) {
      setWorkflow((prev) => ({
        ...prev,
        steps: removeBranchStepFromCondition(prev.steps, branchLocation, stepId),
      }));
      if (editingStep?.id === stepId) {
        setEditingStep(null);
        setEditingBranchContext(null);
        setShowEmailEditor(false);
        setShowSMSEditor(false);
        setShowWaitEditor(false);
        setShowBackendActionEditor(false);
      }
      setHasUnsavedChanges(true);
      return;
    }

    const grouped = buildDisplaySteps(workflow.steps, { advancedMode: true }).display
      .find((item) => item.type === 'group' && item.stepIds.includes(stepId));
    setWorkflow((prev) => ({
      ...prev,
      steps: grouped
        ? prev.steps.filter((s) => !grouped.stepIds.includes(s.id))
        : prev.steps.filter((s) => s.id !== stepId),
    }));
    if (editingStep?.id === stepId) {
      setEditingStep(null);
      setEditingBranchContext(null);
      setShowEmailEditor(false);
      setShowSMSEditor(false);
      setShowWaitEditor(false);
      setShowBackendActionEditor(false);
    }
    setHasUnsavedChanges(true);
  };

  const handleBranchStepContextAction = (action, stepId, branchContext) => {
    if (action === 'delete') {
      handleRemoveStep(stepId);
      return;
    }
    if (action === 'copy' && branchContext) {
      const { steps: nextSteps } = duplicateBranchStepAfter(
        workflow.steps,
        branchContext,
        stepId,
      );
      setWorkflow((prev) => ({ ...prev, steps: nextSteps }));
      setHasUnsavedChanges(true);
    }
  };

  const handleStepsChange = (nextSteps) => {
    recordWorkflowChange('Changed workflow steps');
    setWorkflow((prev) => ({
      ...prev,
      steps: normalizeWorkflowSteps(nextSteps),
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    // Never save while the initial GET is still loading; an empty draft state
    // must not overwrite the persisted trigger.
    if (loading) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      const triggerFields = buildTriggerSaveFields(workflow);
      // TEMP DEBUG (trigger persistence investigation) - remove later
      console.debug(
        '[trigger-persistence] saving workflow',
        workflowId,
        triggerFields.trigger?.key ?? null,
      );
      const payload = {
        name: workflow.name,
        description: workflow.description,
        category: workflow.category,
        ...triggerFields,
        sender_scope: workflow.sender_scope,
        steps: workflow.steps,
        canvas_notes: workflow.canvas_notes || [],
        settings: workflow.settings,
      };

      if (isNewWorkflow) {
        const res = await axios.post(`${API}/platform/system-automations/workflows`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Workflow created');
        navigate(`/platform/automations/workflows/${res.data.workflow.id}/builder`);
      } else {
        await axios.put(`${API}/platform/system-automations/workflows/${workflowId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Workflow saved');
      }
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save workflow:', err);
      setSaveFailed(true);
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (isNewWorkflow) {
      await handleSave();
      return;
    }

    try {
      const { isForgotPasswordWorkflow, validateForgotPasswordWorkflow } = await import(
        '../../../utils/forgotPasswordWorkflowValidation'
      );
      if (isForgotPasswordWorkflow(workflow)) {
        const validation = validateForgotPasswordWorkflow(workflow);
        if (!validation.canActivate) {
          toast.error(validation.errors[0] || 'Forgot Password workflow is invalid');
          return;
        }
      }
    } catch (_) {
      // Validation util optional — backend still enforces
    }
    
    try {
      await axios.post(`${API}/platform/system-automations/workflows/${workflowId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflow(prev => ({ ...prev, status: 'active' }));
      toast.success('Workflow activated');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to activate workflow');
    }
  };

  const handlePause = async () => {
    try {
      await axios.post(`${API}/platform/system-automations/workflows/${workflowId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflow(prev => ({ ...prev, status: 'paused' }));
      toast.success('Workflow paused');
    } catch (err) {
      toast.error('Failed to pause workflow');
    }
  };

  const handleDownload = useCallback(async () => {
    if (isNewWorkflow || !workflowId || isDownloadingWorkflow) {
      if (isNewWorkflow || !workflowId) {
        toast.error('Save the workflow before downloading');
      }
      return false;
    }
    setIsDownloadingWorkflow(true);
    try {
      const { blob, filename } = await downloadPlatformWorkflow(
        token,
        workflowId,
        workflow?.name || 'workflow',
      );
      triggerBrowserDownload(blob, filename);
      toast.success('Workflow downloaded');
      return true;
    } catch (error) {
      toast.error(
        getSafeWorkflowPortableError(error, 'Unable to download this workflow.'),
      );
      return false;
    } finally {
      setIsDownloadingWorkflow(false);
    }
  }, [
    isDownloadingWorkflow,
    isNewWorkflow,
    token,
    workflow?.name,
    workflowId,
  ]);

  const { openFileImport, openUrlImport } = useMemo(
    () => createWorkflowImportOpeners(setWorkflowImportMode, setWorkflowImportOpen),
    [],
  );

  const handleWorkflowImported = useMemo(
    () => createWorkflowImportedHandler({
      setWorkflowImportOpen,
      navigate,
      getBuilderPath: getPlatformBuilderPath,
      toastSuccess: toast.success,
    }),
    [navigate],
  );

  const handleTogglePublish = async (publish) => {
    const isPublished = workflow.status === 'active';
    if (publish === isPublished) return;

    recordWorkflowChange(publish ? 'Published workflow' : 'Set workflow to draft');

    if (publish) {
      await handleActivate();
      return;
    }

    if (workflow.status === 'active') {
      await handlePause();
      return;
    }

    setWorkflow((prev) => ({ ...prev, status: 'draft' }));
    setHasUnsavedChanges(true);
  };

  const editorLayoutFlags = useMemo(() => ({
    variant: 'platform',
    showActionsPanel,
    showTriggerPicker,
    showEmailEditor,
    showSMSEditor,
    showWaitEditor,
    showGrantPortalEditor,
    showRevokePortalEditor,
    showGrantOfferEditor,
    showRevokeOfferEditor,
    showApplyTagEditor,
    showRemoveTagEditor,
    showConditionEditor,
    showNotificationEditor,
    showBackendActionEditor,
    showPreparePasswordResetEditor,
    showFindUserPortalEditor,
    showTriggerEditor,
    showAuditLogEditor,
    showRunTestPanel,
    runTestExpanded,
  }), [
    showActionsPanel,
    showTriggerPicker,
    showEmailEditor,
    showSMSEditor,
    showWaitEditor,
    showGrantPortalEditor,
    showRevokePortalEditor,
    showGrantOfferEditor,
    showRevokeOfferEditor,
    showApplyTagEditor,
    showRemoveTagEditor,
    showConditionEditor,
    showNotificationEditor,
    showBackendActionEditor,
    showPreparePasswordResetEditor,
    showFindUserPortalEditor,
    showTriggerEditor,
    showAuditLogEditor,
    showRunTestPanel,
    runTestExpanded,
  ]);

  const {
    builderRef,
    editorPanelWidth,
    editorOverlayMode,
    getPanelShellProps,
  } = useWorkflowEditorLayout(editorLayoutFlags);

  const panelShellOptions = useMemo(
    () => ({ onOverlayDismiss: closeEditorOverlay }),
    [closeEditorOverlay],
  );

  const tabs = [
    { id: 'builder', label: 'Builder', icon: Zap },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'statistics', label: 'Statistics', icon: TrendingUp },
    { id: 'enrollment', label: 'Enrollment History', icon: Users },
    { id: 'logs', label: 'Execution Logs', icon: BarChart3 },
  ];

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden text-white" data-testid="platform-workflow-builder">
      <WorkflowBuilderChrome
        variant="platform"
        backLabel="Back"
        onBack={() => guardAction(() => navigate('/platform/automations'))}
        workflowName={workflow.name}
        onWorkflowNameFocus={() => {
          nameEditSnapshot.current = workflow.name;
        }}
        onWorkflowNameBlur={() => {
          if (
            nameEditSnapshot.current !== null
            && nameEditSnapshot.current !== workflow.name
          ) {
            undoRedo.recordChange('Renamed workflow', {
              ...workflow,
              name: nameEditSnapshot.current,
            });
          }
          nameEditSnapshot.current = null;
        }}
        onWorkflowNameChange={(name) => {
          setWorkflow((prev) => ({ ...prev, name }));
          setHasUnsavedChanges(true);
        }}
        workflowStatus={workflow.status}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        onSave={handleSave}
        onPause={handlePause}
        onActivate={handleActivate}
        onDownload={handleDownload}
        isDownloading={isDownloadingWorkflow}
        onImportFromFile={openFileImport}
        onImportFromUrl={openUrlImport}
        showPause={workflow.status === 'active'}
        showActivate={workflow.status !== 'active'}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => {
          guardAction(() => {
            closeAllPanels();
            setActiveTab(tabId);
          });
        }}
        undoRedo={undoRedo}
        onOpenRunTest={() => {
          closeAllPanels();
          setShowRunTestPanel(true);
        }}
        isPublished={workflow.status === 'active'}
        onTogglePublish={handleTogglePublish}
        publishDisabled={saving}
      />

      {/* Main Content */}
      <div ref={builderRef} className="flex min-h-0 flex-1 overflow-hidden">
        {activeTab === 'builder' ? (
          <WorkflowBuilderUtilityShell
            workflow={workflow}
            setWorkflow={setWorkflow}
            workflowId={workflowId}
            token={token}
            apiBase={`${API}/platform/system-automations/workflows`}
            systemApiBase={`${API}/platform/system-automations`}
            executionPathView={executionPathView}
            onExitExecutionPath={() => setExecutionPathView(null)}
            onUnsavedChange={() => setHasUnsavedChanges(true)}
            onWorkflowReload={async () => {
              if (isNewWorkflow || !token) return;
              try {
                const res = await axios.get(`${API}/platform/system-automations/workflows/${workflowId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                const wf = res.data;
                setWorkflow((prev) => ({
                  ...prev,
                  name: wf.name || prev.name,
                  description: wf.description || prev.description,
                  status: wf.status || prev.status,
                  category: wf.category || prev.category,
                  steps: normalizeWorkflowSteps(wf.steps || []),
                  canvas_notes: wf.canvas_notes || [],
                  updated_at: wf.updated_at || prev.updated_at,
                }));
              } catch (err) {
                console.error('Failed to reload workflow:', err);
              }
            }}
            contentClassName="p-6"
            variant="platform"
            editorPanelWidth={editorPanelWidth}
            editorOverlayMode={editorOverlayMode}
            onEditorOverlayDismiss={closeEditorOverlay}
            editorPanel={(
              <>
                {showActionsPanel && (
                  <WorkflowActionsPanel
                    isOpen={showActionsPanel}
                    {...getPanelShellProps('actions', panelShellOptions)}
                    theme="dark"
                    advancedMode
                    allowAdvancedActions
                    allowDataTools
                    extraGroups={PLATFORM_ACTION_EXTRA_GROUPS}
                    onClose={closeAllPanels}
                    onSelectAction={handleSelectAction}
                  />
                )}

                {showTriggerPicker && (
                  <PlatformTriggerPicker
                    isOpen={showTriggerPicker}
                    {...getPanelShellProps('trigger-picker', panelShellOptions)}
                    onClose={closeAllPanels}
                    onSelectTrigger={handleSelectTrigger}
                    activeTriggers={normalizeWorkflowTriggers(workflow)}
                    editingTriggerId={editingTriggerId}
                  />
                )}

                {showEmailEditor && (
                  <EmailActionComposer
                    isOpen={showEmailEditor}
                    {...getPanelShellProps('email', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    scope="platform"
                    token={token}
                    workflowId={workflowId}
                  />
                )}

                {showSMSEditor && (
                  <SMSNodeEditor
                    isOpen={showSMSEditor}
                    {...getPanelShellProps('sms', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    onDelete={handleRemoveStep}
                    token={token}
                    workflowId={workflowId}
                  />
                )}

                {showWaitEditor && (
                  <WaitEditor
                    isOpen={showWaitEditor}
                    {...getPanelShellProps('wait', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                  />
                )}

                {showGrantPortalEditor && (
                  <GrantPortalAccessEditor
                    isOpen={showGrantPortalEditor}
                    {...getPanelShellProps('grant-portal', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showRevokePortalEditor && (
                  <RevokePortalAccessEditor
                    isOpen={showRevokePortalEditor}
                    {...getPanelShellProps('revoke-portal', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showGrantOfferEditor && (
                  <GrantOfferAccessEditor
                    isOpen={showGrantOfferEditor}
                    {...getPanelShellProps('grant-offer', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showRevokeOfferEditor && (
                  <RevokeOfferAccessEditor
                    isOpen={showRevokeOfferEditor}
                    {...getPanelShellProps('revoke-offer', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showApplyTagEditor && (
                  <ApplyTagEditor
                    isOpen={showApplyTagEditor}
                    {...getPanelShellProps('apply-tag', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showRemoveTagEditor && (
                  <RemoveTagEditor
                    isOpen={showRemoveTagEditor}
                    {...getPanelShellProps('remove-tag', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showConditionEditor && (
                  <IfElseConditionEditor
                    isOpen={showConditionEditor}
                    {...getPanelShellProps('condition', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                    workflowSteps={workflow.steps}
                    workflowTrigger={workflow.trigger}
                  />
                )}

                {showNotificationEditor && (
                  <InternalNotificationEditor
                    isOpen={showNotificationEditor}
                    {...getPanelShellProps('notification', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={handleSaveStep}
                    token={token}
                  />
                )}

                {showBackendActionEditor && (
                  <BackendActionEditor
                    isOpen={showBackendActionEditor}
                    {...getPanelShellProps('backend-action', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={isNewWorkflow ? handleSaveStep : handlePersistedStepSave}
                    workflowId={workflowId}
                    token={token}
                    isNewWorkflow={isNewWorkflow}
                  />
                )}

                {showPreparePasswordResetEditor && (
                  <PreparePasswordResetEditor
                    isOpen={showPreparePasswordResetEditor}
                    {...getPanelShellProps('prepare-password-reset', panelShellOptions)}
                    onClose={closeAllPanels}
                    groupItem={editingGroup}
                    onSave={handleSaveGroup}
                    advancedMode
                  />
                )}

                {showFindUserPortalEditor && (
                  <FindUserPortalEditor
                    isOpen={showFindUserPortalEditor}
                    {...getPanelShellProps('find-user-portal', panelShellOptions)}
                    onClose={closeAllPanels}
                    groupItem={editingGroup}
                    onSave={handleSaveGroup}
                    advancedMode
                  />
                )}

                {showTriggerEditor && (
                  <TriggerEditor
                    isOpen={showTriggerEditor}
                    {...getPanelShellProps('trigger-editor', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={isNewWorkflow ? handleSaveStep : handlePersistedStepSave}
                    workflowId={workflowId}
                    token={token}
                    isNewWorkflow={isNewWorkflow}
                  />
                )}

                {showAuditLogEditor && (
                  <AuditLogEditor
                    isOpen={showAuditLogEditor}
                    {...getPanelShellProps('audit-log', panelShellOptions)}
                    onClose={closeAllPanels}
                    step={editingStep}
                    onSave={isNewWorkflow ? handleSaveStep : handlePersistedStepSave}
                    workflowId={workflowId}
                    token={token}
                    isNewWorkflow={isNewWorkflow}
                  />
                )}

                {showRunTestPanel && (
                  <WorkflowRunTestPanel
                    isOpen={showRunTestPanel}
                    {...getPanelShellProps('run-test', panelShellOptions)}
                    onClose={closeAllPanels}
                    onExpandedChange={setRunTestExpanded}
                    token={token}
                    workflowId={workflowId}
                    scope="platform"
                    executionAvailable={!isNewWorkflow}
                  />
                )}
              </>
            )}
          >
            {({ isBuilderReadOnly, highlightedStepId, pathStepStates }) => (
              <WorkflowBuilderMainChain
                width={actionLaneLayout.width}
                wrapperWidth={actionLaneLayout.wrapperWidth}
                centerX={actionLaneLayout.centerX}
                globalCenterX={actionLaneLayout.globalCenterX}
              >
                <WorkflowTriggerCanvasSection
                  workflow={workflow}
                  readOnly={isBuilderReadOnly}
                  workflowId={workflowId}
                  token={token}
                  onTriggersChange={handleTriggersChange}
                  onAddTrigger={openAddTriggerPicker}
                  onEditTrigger={openEditTriggerPicker}
                  onConnectorLayoutChange={handleTriggerConnectorLayoutChange}
                />

                <WorkflowBuilderStepList
                  steps={workflow.steps}
                  theme="dark"
                  readOnly={isBuilderReadOnly}
                  columnWidth={actionLaneLayout.width}
                  connectorCenterX={actionLaneLayout.centerX}
                  columnOffset={actionLaneLayout.offset}
                  pathStepStates={pathStepStates}
                  selectedStepId={editingStep?.id}
                  selectedGroupId={editingGroup?.groupId}
                  advancedMode
                  onEditGroup={openEditGroup}
                  onStepsChange={handleStepsChange}
                  onAddStep={handleAddStep}
                  onRemove={handleRemoveStep}
                  renderStep={(step, index, helpers) => (
                    <StepNode
                      step={step}
                      index={index}
                      onRemove={handleRemoveStep}
                      onEdit={openStepEditor}
                      isMoving={helpers.isMovingStep(step.id)}
                      isSelected={helpers.isSelected(step.id)}
                      onDragHandleStart={helpers.onDragHandleStart}
                      onContextMenuAction={helpers.onContextMenuAction}
                      hasClipboard={helpers.hasClipboard}
                      readOnly={isBuilderReadOnly}
                      highlighted={highlightedStepId === step.id}
                      pathState={pathStepStates?.[step.id]}
                    />
                  )}
                  renderConditionBranch={(step, index, _helpers, branchHelpers) => (
                    isConditionStep(step) ? (
                      <WorkflowConditionBranchCanvas
                        step={step}
                        theme="dark"
                        readOnly={isBuilderReadOnly}
                        selectedBranchStepId={editingBranchContext ? editingStep?.id : null}
                        onGraphHeightChange={branchHelpers?.onGraphHeightChange}
                        onAddStepToBranch={handleAddBranchStep}
                        renderBranchStep={(branchStep, branchContext) => (
                          <StepNode
                            step={branchStep}
                            onRemove={handleRemoveStep}
                            onEdit={openStepEditor}
                            isMoving={false}
                            isSelected={branchContext.isSelected}
                            onDragHandleStart={() => {}}
                            onContextMenuAction={(action, stepId) => (
                              handleBranchStepContextAction(action, stepId, branchContext)
                            )}
                            hasClipboard={false}
                            readOnly={isBuilderReadOnly}
                            columnWidth={BRANCH_CARD_MIN_WIDTH}
                            isBranchStep
                          />
                        )}
                      />
                    ) : null
                  )}
                />

                {shouldRenderMainChainEndNode(workflow.steps) ? (
                <div
                  className="relative"
                  style={{
                    width: actionLaneLayout.width,
                    marginLeft: actionLaneLayout.offset,
                    height: 48,
                  }}
                  data-workflow-canvas-node
                  data-workflow-action-lane-end
                  data-workflow-main-center-x={actionLaneLayout.centerX}
                  data-workflow-end-center-x={actionLaneLayout.globalCenterX}
                  data-workflow-main-global-center-x={actionLaneLayout.globalCenterX}
                >
                  <div
                    className="absolute rounded-full bg-zinc-800 px-6 py-3 text-sm font-medium text-zinc-400"
                    style={{ left: actionLaneLayout.centerX, transform: 'translateX(-50%)' }}
                  >
                    End of Workflow
                  </div>
                </div>
                ) : null}
              </WorkflowBuilderMainChain>
            )}
          </WorkflowBuilderUtilityShell>
        ) : (
        <div
          className="flex-1 overflow-y-auto bg-slate-50 p-6"
        >
          {activeTab === 'settings' && (
            <WorkflowSettingsTab
              workflow={workflow}
              settings={normalizeWorkflowSettings(workflow.settings)}
              onSettingsChange={handleSettingsChange}
              onMetaChange={handleSettingsMetaChange}
              token={token}
            />
          )}

          {activeTab === 'statistics' && !isNewWorkflow && (
            <WorkflowStatisticsPanel workflow={workflow} token={token} />
          )}

          {activeTab === 'statistics' && isNewWorkflow && (
            <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <TrendingUp className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="font-medium text-slate-800">No statistics yet</p>
              <p className="mt-1 text-sm text-slate-500">Save this workflow to start collecting email performance statistics.</p>
            </div>
          )}

          {activeTab === 'enrollment' && !isNewWorkflow && (
            <WorkflowEnrollmentHistoryTab
              token={token}
              workflowId={workflowId}
              onViewExecutionPath={handleViewExecutionPath}
            />
          )}

          {activeTab === 'enrollment' && isNewWorkflow && (
            <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <Users className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="font-medium text-slate-800">No enrollments yet</p>
              <p className="mt-1 text-sm text-slate-500">Save this workflow to start tracking enrollment history.</p>
            </div>
          )}

          {activeTab === 'logs' && !isNewWorkflow && (
            <WorkflowExecutionLogsTab
              token={token}
              workflowId={workflowId}
              onViewExecutionPath={handleViewExecutionPath}
            />
          )}

          {activeTab === 'logs' && isNewWorkflow && (
            <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <BarChart3 className="mx-auto mb-3 text-slate-400" size={40} />
              <p className="font-medium text-slate-800">No execution logs yet</p>
              <p className="mt-1 text-sm text-slate-500">Logs will appear once this workflow starts running.</p>
            </div>
          )}
        </div>
        )}

      </div>

      <WorkflowImportDialog
        open={workflowImportOpen}
        onOpenChange={setWorkflowImportOpen}
        mode={workflowImportMode}
        scope="platform"
        token={token}
        onImported={handleWorkflowImported}
      />
    </div>
  );
};

const PlatformWorkflowBuilderPage = () => (
  <ActionEditorUnsavedProvider>
    <PlatformWorkflowBuilderPageInner />
  </ActionEditorUnsavedProvider>
);

export default PlatformWorkflowBuilderPage;
